/**
 * IAM Organization Service
 * Handles CRUD operations for organizations via the IAM API
 */

import axios from 'axios';

// Get auth token from localStorage
const getAuthToken = () => {
    try {
        const tokenData = localStorage.getItem('oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app');
        if (tokenData) {
            const parsedData = JSON.parse(tokenData);
            return parsedData.access_token || '';
        }
        return '';
    } catch (error) {
        console.error('Error retrieving access token:', error);
        return '';
    }
};

// Create IAM API instance
const iamApi = axios.create({
    baseURL: import.meta.env.MODE === 'development' ? '/iam/api' : '/iam/api',
});

// Add request interceptor to include auth token
iamApi.interceptors.request.use(
    (config) => {
        const token = getAuthToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor
iamApi.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app');
            window.location.href = '/';
        }
        return Promise.reject(error);
    }
);

// Types
export interface Organization {
    id: string;
    name: string;
    displayName?: string;
    url?: string;
    domains?: string[];
    realm?: string;
    attributes?: Record<string, string[]>;
}

export interface OrganizationListResponse {
    content: Organization[];
    count: number;
    page: number;
    size: number;
}

export interface CreateOrganizationRequest {
    name: string;
    displayName?: string;
    url?: string;
    domains?: string[];
}

export interface UpdateOrganizationRequest {
    name?: string;
    displayName?: string;
    url?: string;
    domains?: string[];
}

// Organization Service
export class OrganizationService {
    private basePath = '/organizations';

    /**
     * Get paginated list of organizations
     */
    async getOrganizations(
        search: string = '',
        page: number = 0,
        size: number = 100
    ): Promise<OrganizationListResponse> {
        const response = await iamApi.get<OrganizationListResponse>(this.basePath, {
            params: { search, page, size },
        });
        return response.data;
    }

    /**
     * Get a single organization by ID
     */
    async getOrganization(orgId: string): Promise<Organization> {
        const response = await iamApi.get<Organization>(`${this.basePath}/${orgId}`);
        return response.data;
    }

    /**
     * Create a new organization
     */
    async createOrganization(data: CreateOrganizationRequest): Promise<Organization> {
        const response = await iamApi.post<Organization>(`${this.basePath}/create`, data);
        return response.data;
    }

    /**
     * Update an existing organization
     */
    async updateOrganization(orgId: string, data: UpdateOrganizationRequest): Promise<Organization> {
        const response = await iamApi.put<Organization>(`${this.basePath}/${orgId}`, data);
        return response.data;
    }

    /**
     * Delete an organization
     */
    async deleteOrganization(orgId: string): Promise<void> {
        await iamApi.delete(`${this.basePath}/${orgId}`);
    }
}

// Singleton instance
let organizationServiceInstance: OrganizationService | null = null;

export function getOrganizationService(): OrganizationService {
    if (!organizationServiceInstance) {
        organizationServiceInstance = new OrganizationService();
    }
    return organizationServiceInstance;
}

export default OrganizationService;
